import React from 'react';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <header className="border-b border-gray-800 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="terms-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <img src="/logo.png" alt="TraceMetrics" className="h-10 w-auto" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Términos y Condiciones de Uso</h1>
        <p className="text-gray-500 mb-10">Última actualización: Marzo 2026</p>

        <div className="prose prose-invert max-w-none space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Información General</h2>
            <p>
              Estos Términos y Condiciones de Uso ("Términos") regulan el acceso y uso de la plataforma 
              TraceMetrics, operada por <strong>Noakai LLC</strong>, una sociedad constituida conforme a 
              las leyes de los Estados Unidos de América ("Noakai", "nosotros", "nuestro").
            </p>
            <p>
              Al registrarse, acceder o utilizar TraceMetrics ("la Plataforma", "el Servicio"), usted 
              ("el Usuario", "usted") acepta quedar vinculado por estos Términos, nuestra Política de 
              Privacidad y cualquier otra política o directriz incorporada por referencia. Si no está 
              de acuerdo con alguna disposición, no debe utilizar el Servicio.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Descripción del Servicio</h2>
            <p>
              TraceMetrics es una plataforma de análisis de operaciones de trading que permite a los 
              usuarios cargar archivos de datos de plataformas de trading (incluyendo, pero no limitado 
              a, MEXC y Quantfury) para obtener estadísticas, gráficos y métricas de rendimiento. 
              El Servicio incluye:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Análisis de PNL (ganancias y pérdidas) diario, mensual y acumulado.</li>
              <li>Calendarios de trading, comparativas y tendencias de activos.</li>
              <li>Exportación de informes en formato PDF.</li>
              <li>Comparativa de rendimiento contra indicadores de mercado (ej. Bitcoin).</li>
              <li>Gestión de cuenta de usuario con planes gratuitos y de pago.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Registro y Cuenta de Usuario</h2>
            <p>
              Para acceder al Servicio, usted debe registrarse proporcionando información veraz, 
              completa y actualizada, incluyendo su nombre y dirección de correo electrónico. 
              Usted es responsable de:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Mantener la confidencialidad de sus credenciales de acceso.</li>
              <li>Toda la actividad que ocurra bajo su cuenta.</li>
              <li>Notificarnos inmediatamente de cualquier uso no autorizado de su cuenta.</li>
            </ul>
            <p>
              Nos reservamos el derecho de suspender o cancelar cuentas que violen estos Términos 
              o que muestren actividad fraudulenta.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Planes y Pagos</h2>
            <p>
              TraceMetrics ofrece un plan gratuito con funcionalidades limitadas y planes de pago 
              (mensual, anual y de por vida) que desbloquean todas las funcionalidades. Los pagos 
              se procesan a través de Stripe, un procesador de pagos de terceros.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Los precios están sujetos a cambios con previo aviso.</li>
              <li>Los planes de suscripción se renuevan automáticamente salvo cancelación.</li>
              <li>No se ofrecen reembolsos una vez procesado el pago, salvo que la ley aplicable lo requiera.</li>
              <li>Los códigos promocionales están sujetos a condiciones específicas de cada promoción.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Uso Aceptable</h2>
            <p>El Usuario se compromete a no:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Utilizar el Servicio para fines ilegales o no autorizados.</li>
              <li>Intentar acceder a sistemas, datos o cuentas de otros usuarios sin autorización.</li>
              <li>Cargar archivos maliciosos, virus o código dañino.</li>
              <li>Reproducir, duplicar, copiar, vender o revender el Servicio sin autorización expresa.</li>
              <li>Utilizar bots, scrapers u otros medios automatizados para acceder al Servicio.</li>
              <li>Interferir o interrumpir la integridad o el rendimiento del Servicio.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Propiedad Intelectual</h2>
            <p>
              Todo el contenido, diseño, logotipos, software, código fuente, algoritmos y materiales 
              del Servicio son propiedad exclusiva de Noakai LLC o sus licenciantes y están protegidos 
              por las leyes de propiedad intelectual aplicables. Ninguna disposición de estos Términos 
              otorga al Usuario licencia o derecho alguno sobre nuestra propiedad intelectual, salvo 
              el derecho limitado de usar el Servicio conforme a estos Términos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Datos del Usuario</h2>
            <p>
              Los archivos de trading cargados por el Usuario son procesados exclusivamente para 
              proporcionar los análisis solicitados. Noakai LLC no comparte, vende ni comercializa 
              los datos de trading individuales de los usuarios con terceros.
            </p>
            <p>
              El tratamiento de datos personales se rige por nuestra Política de Privacidad, la cual 
              forma parte integral de estos Términos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Limitación de Responsabilidad</h2>
            <p>
              <strong>EL SERVICIO SE PROPORCIONA "TAL CUAL" Y "SEGÚN DISPONIBILIDAD".</strong> Noakai LLC no garantiza que:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>El Servicio estará disponible de forma ininterrumpida o libre de errores.</li>
              <li>Los resultados obtenidos serán precisos o completos.</li>
              <li>Los defectos del Servicio serán corregidos.</li>
            </ul>
            <p>
              TraceMetrics es una herramienta de análisis y <strong>no constituye asesoramiento financiero, 
              de inversión ni de trading</strong>. Las decisiones de inversión son responsabilidad exclusiva 
              del Usuario. Noakai LLC no será responsable de pérdidas financieras derivadas del uso 
              o interpretación de los datos proporcionados por el Servicio.
            </p>
            <p>
              EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY APLICABLE, LA RESPONSABILIDAD TOTAL DE NOAKAI LLC 
              POR CUALQUIER RECLAMACIÓN RELACIONADA CON EL SERVICIO NO EXCEDERÁ EL IMPORTE PAGADO POR 
              EL USUARIO EN LOS ÚLTIMOS DOCE (12) MESES.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Indemnización</h2>
            <p>
              El Usuario acepta indemnizar, defender y mantener indemne a Noakai LLC, sus directores, 
              empleados y agentes frente a cualquier reclamación, daño, pérdida o gasto (incluyendo 
              honorarios de abogados) derivado del uso del Servicio, la violación de estos Términos 
              o la infracción de derechos de terceros por parte del Usuario.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Modificaciones</h2>
            <p>
              Noakai LLC se reserva el derecho de modificar estos Términos en cualquier momento. 
              Las modificaciones serán efectivas desde su publicación en la Plataforma. El uso 
              continuado del Servicio tras la publicación de cambios constituye la aceptación 
              de los Términos modificados. Se notificará a los usuarios por correo electrónico 
              de cualquier cambio sustancial.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Terminación</h2>
            <p>
              Noakai LLC puede suspender o terminar el acceso del Usuario al Servicio en cualquier 
              momento, por cualquier motivo, incluyendo el incumplimiento de estos Términos. 
              El Usuario puede cancelar su cuenta en cualquier momento contactando a nuestro 
              equipo de soporte. Tras la terminación, las secciones relativas a propiedad intelectual, 
              limitación de responsabilidad e indemnización seguirán vigentes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Ley Aplicable y Jurisdicción</h2>
            <p>
              Estos Términos se regirán e interpretarán de conformidad con las leyes de los 
              Estados Unidos de América. Para la resolución de cualquier controversia, las 
              partes se someten a la jurisdicción de los tribunales competentes del estado 
              donde Noakai LLC tiene su domicilio social, sin perjuicio de los derechos que 
              puedan corresponder al Usuario conforme a la legislación de su país de residencia.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Disposiciones Generales</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Si alguna disposición de estos Términos fuese declarada nula o inaplicable, las restantes disposiciones mantendrán su plena vigencia.</li>
              <li>La renuncia a cualquier derecho bajo estos Términos no constituirá una renuncia futura.</li>
              <li>Estos Términos, junto con la Política de Privacidad, constituyen el acuerdo completo entre el Usuario y Noakai LLC.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">14. Contacto</h2>
            <p>
              Para cualquier consulta relacionada con estos Términos, puede contactarnos a través
              del formulario de contacto disponible en nuestra página web 
              (<strong>tracemetrics.online</strong>), en la sección "Contacto".
            </p>
            <ul className="list-none space-y-1">
              <li><strong>Empresa:</strong> Noakai LLC</li>
              <li><strong>Plataforma:</strong> TraceMetrics</li>
              <li><strong>Canal de contacto:</strong> Formulario web en tracemetrics.online</li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="border-t border-gray-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} TraceMetrics · Noakai LLC. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
